import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {Spin} from 'antd';
import VStack from '../../common/Layout/VStack';
import AdvanceSelect from '../../common/AdvanceSelect';
import FormInput from '../../common/FormInput';
import QuestionsSection from '../../common/questions/QuestionsSection';
import {fetchApi} from '../../../services/HTTP';
import {t} from '../../../services/i18n';
import {dbDateFormat} from '../../../services/Helpers';
import {QuestionTypes} from '../../../Configs/DatabaseConsts';
import {APPOINTMENT_DOCUMENTATION_FORM} from '../../../services/ConstHelper';
import {openModal, STEPPER_CONFIRM_MODAL} from '../../../redux/modalManagerSlice';

const DigitalFormsFiller = ({digitalForms, savedFormsIds, boxCategoryId, onChange, errors}) => {
    const dispatch = useDispatch();
    const documentationFormLinks = useSelector((state) => state.box.box.settings?.[APPOINTMENT_DOCUMENTATION_FORM]);
    const [documentationForms, setDocumentationForms] = useState([]);
    const [isFormsLoaded, setIsFormsLoaded] = useState(false);
    const selectedForms = useMemo(() => digitalForms ?? [], [digitalForms]);
    const formOptions = useMemo(() => {
        const categoryLinks = (documentationFormLinks ?? []).filter((link) => boxCategoryId && (link.properties?.displayMode === 'all' || link.properties?.box_categories?.includes(boxCategoryId)));
        const linkedGroupsIds = categoryLinks.map((link) => link.properties?.groups_id);
        return documentationForms.filter((form) => linkedGroupsIds.includes(form.groups_id)).map((form) => ({...form, value: form.id, label: form.name}));
    }, [documentationForms, documentationFormLinks, boxCategoryId]);
    const savedFormsOptions = useMemo(() => selectedForms.filter((selectedForm) => !formOptions.some((option) => option.value === selectedForm.digital_form.id)).map((selectedForm) => ({value: selectedForm.digital_form.id, label: selectedForm.digital_form.name})), [selectedForms, formOptions]);
    const allOptions = useMemo(() => [...formOptions, ...savedFormsOptions], [formOptions, savedFormsOptions]);
    const selectedOptions = useMemo(() => allOptions.filter((option) => selectedForms.some((selectedForm) => selectedForm.digital_form.id === option.value)), [selectedForms, allOptions]);

    useEffect(() => {
        getDocumentationForms();
    }, []);

    const getDocumentationForms = async () => {
        const forms = await fetchApi('digitalForm/getDocumentationForms', 'GET', null, false, true);
        setDocumentationForms(forms ?? []);
        setIsFormsLoaded(true);
    };

    const loadForm = async (formId) => {
        const form = await fetchApi(`getDigitalForm/${formId}`, 'GET', null, true, true);
        const questionsRow = form?.digital_form_builder?.find((row) => row.property_type === 'questions');
        return {digital_form: form.digital_form, form: {questions: [...(questionsRow?.digital_form_question ?? [])].sort((first, second) => first.order - second.order).map((question) => (question.type === QuestionTypes.DATE ? {...question, user_answer: moment().format(dbDateFormat)} : question))}};
    };

    const hasAnswers = (selectedForm) => selectedForm.form.questions.some((question) => !!question.user_answer || !!question.checked_array?.length);

    const changeSelectedForms = async (selection) => {
        const selectedIds = (selection ?? []).map((option) => option.value);
        const addedForms = await Promise.all(selectedIds.filter((id) => !selectedForms.some((selectedForm) => selectedForm.digital_form.id === id)).map(loadForm));
        const applySelection = () => onChange(selectedIds.map((id) => selectedForms.find((selectedForm) => selectedForm.digital_form.id === id) ?? addedForms.find((addedForm) => addedForm.digital_form.id === id)));
        const removedForms = selectedForms.filter((selectedForm) => !selectedIds.includes(selectedForm.digital_form.id));
        if (removedForms.some((removedForm) => savedFormsIds.includes(removedForm.digital_form.id) && hasAnswers(removedForm))) {
            dispatch(openModal({modalName: STEPPER_CONFIRM_MODAL, props: {header: t('areYouSure'), subheader: t('digital-form-remove-subheader'), confirmText: t('confirmDeleting'), onConfirm: applySelection}}));
        } else {
            applySelection();
        }
    };

    // "read-only" when the form has a newer version or when the form is deleted
    const isReadOnly = (selectedForm) => documentationForms.some((form) => form.groups_id === selectedForm.digital_form.groups_id && form.id !== selectedForm.digital_form.id) || !documentationForms.some((form) => form.id === selectedForm.digital_form.id);

    const changeQuestion = (formId, questionIndex, updatedQuestion) => onChange(selectedForms.map((selectedForm) => (selectedForm.digital_form.id === formId ? {...selectedForm, form: {...selectedForm.form, questions: selectedForm.form.questions.map((question, index) => (index === questionIndex ? updatedQuestion : question))}} : selectedForm)));

    return (
        <VStack>
            <FormInput label={t('digital-forms-select-label')} width={'100%'}>
                <AdvanceSelect isMulti placeholder={t('digital-form-add-placeholder')} options={allOptions} initValue={selectedOptions} onChange={changeSelectedForms} />
            </FormInput>
            {!isFormsLoaded && <Spin size={'small'} style={{alignSelf: 'center'}} />}
            {isFormsLoaded && selectedForms.map((selectedForm, formIndex) => (
                <QuestionsSection key={selectedForm.digital_form.id} title={selectedForms.length > 1 ? selectedForm.digital_form.name : undefined} questions={selectedForm.form.questions} disabled={isReadOnly(selectedForm)} errors={errors?.[formIndex]?.form?.questions} onChange={(questionIndex, updatedQuestion) => changeQuestion(selectedForm.digital_form.id, questionIndex, updatedQuestion)} />
            ))}
        </VStack>
    );
};

export default DigitalFormsFiller;
